import React, {
  memo,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import cn from 'classnames';
import { useFormik } from 'formik';

import Button from '@/components/UI/Button';
import Chosen from '@/components/UI/Chosen';
import InputText from '@/components/UI/InputText';

interface FilterChosenContent {
  placeholder?: string;
  multiple?: string;
}

interface FilterContent {
  sector?: FilterChosenContent;
  city?: FilterChosenContent;
  rental_period?: FilterChosenContent;
  property_type?: FilterChosenContent;
  bedrooms?: FilterChosenContent;
  budget?: FilterChosenContent;
  collection?: FilterChosenContent;
  ref?: FilterChosenContent;
  search?: string;
  reset?: string;
}

interface FilterOption {
  value: string | number;
  name: string;
  slug?: string;
  relation?: Array<string | number>;
}

interface FilterOptions {
  sector?: FilterOption[];
  city?: FilterOption[];
  rental_period?: FilterOption[];
  property_type?: FilterOption[];
  bedrooms?: FilterOption[];
  budget?: FilterOption[];
  collection?: FilterOption[];
}

type FilterSelected = Partial<{
  sector: Array<string | number>;
  city: Array<string | number>;
  collection: Array<string | number>;
  rental_period: Array<string | number>;
  property_type: Array<string | number>;
  bedrooms: Array<string | number>;
  budget: Array<string | number>;
  ref: string;
}>;

interface FiltersFormValues {
  sector: Array<string | number>;
  city: Array<string | number>;
  collection: Array<string | number>;
  rental_period: Array<string | number>;
  property_type: Array<string | number>;
  bedrooms: Array<string | number>;
  budget: Array<string | number>;
  ref: string;
}

type ArrayField = Exclude<keyof FiltersFormValues, 'ref'>;

interface FiltersProps {
  lang: string;
  content?: FilterContent;
  options?: FilterOptions;
  selected?: FilterSelected;
  type: 'sale' | 'rental';
}

const buildQueryString = (query: Record<string, unknown>) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, val]) => {
    if (val == null || val === '') { return; }
    if (Array.isArray(val)) {
      val.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(val));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const buildPath = (
  lang: string,
  type: string,
  slugs: Record<string, string | number | null>,
  query: Record<string, unknown>
) => {
  let path = lang === 'en' ? '' : `/${lang}`;
  const isSalePage = type === 'sale';

  if (lang === 'fr') {
    path += isSalePage ? '/vente' : '/location';
  } else {
    path += isSalePage ? '/sale' : '/rental';
  }

  if (slugs.property_type) {
    path += `/${slugs.property_type}`;
  }

  if (slugs.sector) {
    path += `/${slugs.sector}`;
  }

  if (slugs.city) {
    path += `/${slugs.city}`;
  }

  if (slugs.collection) {
    path += `/${slugs.collection}`;
  }

  if (slugs.rental_period) {
    path += `/${slugs.rental_period}`;
  }

  return `${path}/${buildQueryString(query)}`;
};

const buildQuery = (selected: FiltersFormValues, options: FilterOptions) => {
  const query: Record<string, unknown> = { ...selected };

  const slugs: Record<string, string | number | null> = {
    sector: null,
    city: null,
    collection: null,
    property_type: null,
    rental_period: null
  };

  if ((query.sector as Array<string | number>).length === 1) {
    const option = (options.sector || []).find((c) => c.value === (query.sector as Array<string | number>)[0]);

    if (option) {
      slugs.sector = option.slug || null;
      query.sector = [];
    }
  }

  if ((query.city as Array<string | number>).length === 1) {
    const option = (options.city || []).find((c) => c.value === (query.city as Array<string | number>)[0]);

    if (option) {
      slugs.city = option.slug || null;
      query.city = [];
    }
  }

  if ((query.collection as Array<string | number>).length === 1) {
    const option = (options.collection || []).find((c) => c.value === (query.collection as Array<string | number>)[0]);

    if (option) {
      slugs.collection = option.slug || null;
      query.collection = [];
    }
  }

  if ((query.property_type as Array<string | number>).length === 1) {
     
    slugs.property_type = (query.property_type as Array<string | number>)[0] as string | number;
    query.property_type = [];
  }

  if ((query.rental_period as Array<string | number>).length === 1) {
     
    slugs.rental_period = (query.rental_period as Array<string | number>)[0] as string | number;
    query.rental_period = [];
  }

  return {
    query,
    slugs
  };
};

const initialState: FiltersFormValues = {
  sector: [],
  city: [],
  collection: [],
  rental_period: [],
  property_type: [],
  bedrooms: [],
  budget: [],
  ref: ''
};

const Filters = ({
  lang,
  content = {},
  options = {},
  selected = {},
  type
}: FiltersProps) => {
  const {
    values,
    resetForm,
    handleSubmit,
    setFieldValue,
    setValues
  } = useFormik<FiltersFormValues>({
    initialValues: initialState,
    onSubmit: (form) => {
      const { slugs, query } = buildQuery(form, options);
      const path = buildPath(lang, type, slugs, query);
      window.location.href = window.location.origin + path;
    }
  });

  const onSingleChange = useCallback((name: ArrayField, value: string | number) => {
    const prevSelected = values[name];
    const hasSelected = prevSelected.indexOf(value) !== -1;
    return setFieldValue(name, hasSelected ? [] : [value]);
  }, [values, setFieldValue]);

  const onMultipleChange = useCallback((name: ArrayField, value: string | number) => {
    const prevSelected = values[name];

    const items = (prevSelected.indexOf(value) === -1)
      ? [...prevSelected, value]
      : prevSelected.filter((item) => item !== value);

    return setFieldValue(name, items);
  }, [
    values,
    setFieldValue
  ]);

  const onSectorChange = useCallback((value: string | number) => {
    onMultipleChange('sector', value);

    setFieldValue('city', []);
  }, [
    setFieldValue,
    onMultipleChange
  ]);

  const citiesOptions = useMemo(() => {
    const { sector } = values;

    if (!sector.length) {
      return options.city;
    }

    const selectedSectors = (options.sector || []).filter((s) => sector.indexOf(s.value) !== -1);
    const cities = selectedSectors.reduce<Array<string | number>>((acc, cur) => [...acc, ...(cur.relation || [])], []);

    return (options.city || []).filter((c) => cities.indexOf(c.value) !== -1);
  }, [
    options,
    values
  ]);

  const onReset = useCallback(() => {
    resetForm({ values: initialState });
  }, [resetForm]);

  useEffect(() => {
    setValues(Object.keys(initialState).reduce<FiltersFormValues>((prev, key) => ({
      ...prev,
      // eslint-disable-next-line max-len
      [key]: (selected as unknown as Record<string, unknown>)[key] || (initialState as unknown as Record<string, unknown>)[key]
    }), { ...initialState }));
  }, [
    selected,
    setValues
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <div className={cn('container', 'filter-wrap', `filter-${type}`)}>
        <div className="row middle-lg">
          <div className="col-lg-3 col-sm-4 col-xs-12 sector">
            <Chosen
              multiple
              maxItems={99}
              content={content.sector}
              options={options.sector}
              selected={values.sector}
              onChange={(value) => onSectorChange(value)}
            />
          </div>

          <div className="col-lg-3 col-sm-4 col-xs-12 city">
            <Chosen
              multiple
              searchable
              maxItems={7}
              content={content.city}
              options={citiesOptions}
              selected={values.city}
              onChange={(value) => onMultipleChange('city', value)}
            />
          </div>

          {(type === 'rental') && (
            <div className="col-lg-3 col-sm-4 col-xs-12 rental_period">
              <Chosen
                content={content.rental_period}
                options={options.rental_period}
                selected={values.rental_period}
                onChange={(value) => onSingleChange('rental_period', value)}
              />
            </div>
          )}

          <div className="col-lg-3 col-sm-4 col-xs-12 property_type">
            <Chosen
              multiple
              content={content.property_type}
              options={options.property_type}
              selected={values.property_type}
              onChange={(value) => onMultipleChange('property_type', value)}
            />
          </div>

          <div className="col-lg-3 col-sm-4 col-xs-12 bedrooms">
            <Chosen
              content={content.bedrooms}
              options={options.bedrooms}
              selected={values.bedrooms}
              onChange={(value) => onSingleChange('bedrooms', value)}
            />
          </div>

          {(type === 'sale') && (
            <div className="col-lg-3 col-sm-4 col-xs-12 budget">
              <Chosen
                multiple
                content={content.budget}
                options={options.budget}
                selected={values.budget}
                onChange={(value) => onMultipleChange('budget', value)}
              />
            </div>
          )}

          <div className="col-lg-3 col-sm-4 col-xs-12 collection">
            <Chosen
              multiple
              searchable
              maxItems={7}
              content={content.collection}
              options={options.collection}
              selected={values.collection}
              onChange={(value) => onMultipleChange('collection', value)}
            />
          </div>

          <div className="col-lg-3 col-sm-4 col-xs-12 reference">
            <InputText
              name="ref"
              placeholder={content.ref?.placeholder}
              type="text"
              value={values.ref}
              onChange={({ target }) => setFieldValue('ref', (target as HTMLInputElement).value)}
            />
          </div>

          <div className="col-lg-3 col-sm-4 col-xs-12 actions">
            <Button
              className="search gold-solid"
              type="submit"
            >
              {content.search}
            </Button>

            <Button
              className="reset"
              title={content.reset}
              onClick={onReset}
            >
              {content.reset}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default memo(Filters);
